import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MdRefresh, MdBarChart, MdConfirmationNumber, MdAccessTime, MdFilterList, MdClear, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { helpdeskTicketApi, helpdeskCategoryApi, helpdeskLookupApi } from '../../../api/helpdeskApi';
import { useNavigate } from 'react-router-dom';
import './HelpdeskAnalytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const STATUS_COLORS = {
  OPEN: '#2196F3',
  ASSIGNED: '#FF9800',
  IN_PROGRESS: '#9C27B0',
  RESOLVED: '#4CAF50',
  CLOSED: '#607D8B',
  REOPENED: '#F44336',
};

const STATUS_LABELS = {
  OPEN: 'Open',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  REOPENED: 'Reopened',
};

const PRIORITY_COLORS = {
  LOW: '#4CAF50',
  MEDIUM: '#FF9800',
  HIGH: '#F44336',
  CRITICAL: '#9C27B0',
};

const CHART_PALETTE = [
  '#704493', '#2196F3', '#FF9800', '#4CAF50', '#F44336',
  '#9C27B0', '#00BCD4', '#607D8B', '#E91E63', '#3F51B5',
];

function HelpdeskAnalytics() {
  const [allTickets, setAllTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  // Filter states
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [cityFilter, setCityFilter] = useState('ALL');
  const [requestTypeFilter, setRequestTypeFilter] = useState('ALL');
  const [assignedFilter, setAssignedFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [tickets, cats, cityList] = await Promise.all([
        helpdeskTicketApi.getAll(),
        helpdeskCategoryApi.getActive().catch(() => []),
        helpdeskLookupApi.getCities().catch(() => []),
      ]);
      setAllTickets(Array.isArray(tickets) ? tickets : []);
      setCategories(Array.isArray(cats) ? cats : []);
      setCities(Array.isArray(cityList) ? cityList : []);
    } catch (err) {
      console.error('Analytics load error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get unique assignees from ticket data
  const assignees = useMemo(() => {
    const map = {};
    allTickets.forEach(t => {
      if (t.assignedTo) {
        map[t.assignedTo.email] = `${t.assignedTo.firstName || ''} ${t.assignedTo.secondName || ''}`.trim();
      }
    });
    return Object.entries(map).map(([email, name]) => ({ email, name }));
  }, [allTickets]);

  // Apply filters
  const filteredTickets = useMemo(() => {
    return allTickets.filter(t => {
      if (statusFilter !== 'ALL' && t.ticketStatus !== statusFilter) return false;
      if (priorityFilter !== 'ALL' && t.priority !== priorityFilter) return false;
      if (categoryFilter !== 'ALL') {
        const catName = t.category?.categoryName || 'Uncategorized';
        if (catName !== categoryFilter) return false;
      }
      if (cityFilter !== 'ALL' && (t.city || 'Unknown') !== cityFilter) return false;
      if (requestTypeFilter !== 'ALL' && t.requestType !== requestTypeFilter) return false;
      if (assignedFilter !== 'ALL') {
        if (assignedFilter === 'UNASSIGNED') {
          if (t.assignedTo) return false;
        } else {
          if (!t.assignedTo || t.assignedTo.email !== assignedFilter) return false;
        }
      }
      if (dateFrom) {
        const ticketDate = new Date(t.createdAt).toISOString().split('T')[0];
        if (ticketDate < dateFrom) return false;
      }
      if (dateTo) {
        const ticketDate = new Date(t.createdAt).toISOString().split('T')[0];
        if (ticketDate > dateTo) return false;
      }
      return true;
    });
  }, [allTickets, statusFilter, priorityFilter, categoryFilter, cityFilter, requestTypeFilter, assignedFilter, dateFrom, dateTo]);

  // Compute analytics from filtered tickets
  const analytics = useMemo(() => {
    const statusCounts = {};
    Object.keys(STATUS_LABELS).forEach(s => { statusCounts[s] = 0; });

    const categoryWise = {};
    const priorityWise = {};
    const cityWise = {};
    const requestTypeWise = {};
    const assigneeWise = {};
    const dailyTrend = {};

    filteredTickets.forEach(t => {
      // Status counts
      if (t.ticketStatus) statusCounts[t.ticketStatus] = (statusCounts[t.ticketStatus] || 0) + 1;

      // Category-wise by status
      const catName = t.category?.categoryName || 'Uncategorized';
      if (!categoryWise[catName]) categoryWise[catName] = {};
      categoryWise[catName][t.ticketStatus] = (categoryWise[catName][t.ticketStatus] || 0) + 1;

      // Priority-wise
      if (t.priority) {
        priorityWise[t.priority] = (priorityWise[t.priority] || 0) + 1;
      }

      // City-wise
      const city = t.city || 'Unknown';
      cityWise[city] = (cityWise[city] || 0) + 1;

      // Request type
      if (t.requestType) {
        requestTypeWise[t.requestType] = (requestTypeWise[t.requestType] || 0) + 1;
      }

      // Assignee workload
      const assigneeName = t.assignedTo
        ? `${t.assignedTo.firstName || ''} ${t.assignedTo.secondName || ''}`.trim()
        : 'Unassigned';
      if (!assigneeWise[assigneeName]) assigneeWise[assigneeName] = { open: 0, resolved: 0 };
      if (['RESOLVED', 'CLOSED'].includes(t.ticketStatus)) {
        assigneeWise[assigneeName].resolved += 1;
      } else {
        assigneeWise[assigneeName].open += 1;
      }

      // Daily trend
      if (t.createdAt) {
        const day = new Date(t.createdAt).toISOString().split('T')[0];
        dailyTrend[day] = (dailyTrend[day] || 0) + 1;
      }
    });

    // Average resolution time (hours)
    let totalResolutionMs = 0;
    let resolvedCount = 0;
    filteredTickets.forEach(t => {
      if (t.resolvedAt && t.createdAt) {
        totalResolutionMs += new Date(t.resolvedAt) - new Date(t.createdAt);
        resolvedCount++;
      }
    });
    const avgResolutionHours = resolvedCount > 0 ? Math.round(totalResolutionMs / resolvedCount / (1000 * 60 * 60)) : 0;

    // Recent unresolved (top 5)
    const recentTickets = filteredTickets
      .filter(t => t.ticketStatus !== 'RESOLVED' && t.ticketStatus !== 'CLOSED')
      .slice(0, 5);

    return {
      statusCounts, categoryWise, priorityWise, cityWise, requestTypeWise,
      assigneeWise, dailyTrend, avgResolutionHours, resolvedCount,
      recentTickets, totalTickets: filteredTickets.length
    };
  }, [filteredTickets]);

  const hasActiveFilters = statusFilter !== 'ALL' || priorityFilter !== 'ALL' || categoryFilter !== 'ALL' ||
    cityFilter !== 'ALL' || requestTypeFilter !== 'ALL' || assignedFilter !== 'ALL' || dateFrom || dateTo;

  const activeFilterCount = [
    statusFilter !== 'ALL', priorityFilter !== 'ALL', categoryFilter !== 'ALL',
    cityFilter !== 'ALL', requestTypeFilter !== 'ALL', assignedFilter !== 'ALL',
    !!dateFrom, !!dateTo
  ].filter(Boolean).length;

  const clearFilters = () => {
    setStatusFilter('ALL');
    setPriorityFilter('ALL');
    setCategoryFilter('ALL');
    setCityFilter('ALL');
    setRequestTypeFilter('ALL');
    setAssignedFilter('ALL');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) {
    return (
      <div className="helpdesk-analytics">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="helpdesk-analytics">
        <div className="error-message">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadData}>
            <MdRefresh /> Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    statusCounts, categoryWise, priorityWise, cityWise, requestTypeWise,
    assigneeWise, dailyTrend, avgResolutionHours, resolvedCount,
    recentTickets, totalTickets
  } = analytics;

  // ===== CHART DATA =====

  // Category-wise grouped bar chart
  const categoryNames = Object.keys(categoryWise);
  const allStatuses = Object.keys(STATUS_LABELS);
  const categoryChartData = {
    labels: categoryNames,
    datasets: allStatuses
      .filter(status => categoryNames.some(cat => (categoryWise[cat]?.[status] || 0) > 0))
      .map(status => ({
        label: STATUS_LABELS[status],
        data: categoryNames.map(cat => categoryWise[cat]?.[status] || 0),
        backgroundColor: STATUS_COLORS[status] + 'CC',
        borderColor: STATUS_COLORS[status],
        borderWidth: 1,
        borderRadius: 4,
      })),
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 } } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
    },
  };

  // Status distribution horizontal bar
  const statusBarData = {
    labels: Object.keys(STATUS_LABELS).map(s => STATUS_LABELS[s]),
    datasets: [{
      data: Object.keys(STATUS_LABELS).map(s => statusCounts[s] || 0),
      backgroundColor: Object.keys(STATUS_LABELS).map(s => STATUS_COLORS[s] + 'CC'),
      borderColor: Object.keys(STATUS_LABELS).map(s => STATUS_COLORS[s]),
      borderWidth: 1,
      borderRadius: 4,
      barThickness: 22,
    }],
  };

  const statusBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: '#f1f5f9' } },
      y: { grid: { display: false }, ticks: { font: { size: 11, weight: 600 } } },
    },
  };

  // Priority donut chart
  const priorityLabels = Object.keys(priorityWise);
  const priorityChartData = {
    labels: priorityLabels.map(p => p.charAt(0) + p.slice(1).toLowerCase()),
    datasets: [{
      data: priorityLabels.map(p => priorityWise[p]),
      backgroundColor: priorityLabels.map(p => PRIORITY_COLORS[p] || '#607D8B'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  // City donut chart
  const cityLabels = Object.keys(cityWise);
  const cityChartData = {
    labels: cityLabels,
    datasets: [{
      data: cityLabels.map(c => cityWise[c]),
      backgroundColor: cityLabels.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  // Request type donut chart
  const typeLabels = Object.keys(requestTypeWise);
  const typeDisplayLabels = typeLabels.map(t =>
    t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  );
  const typeChartData = {
    labels: typeDisplayLabels,
    datasets: [{
      data: typeLabels.map(t => requestTypeWise[t]),
      backgroundColor: ['#704493', '#2196F3', '#FF9800', '#4CAF50'],
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 10, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
    },
    cutout: '65%',
  };

  // Assignee workload bar chart
  const assigneeNames = Object.keys(assigneeWise).filter(n => n !== 'Unassigned');
  const assigneeChartData = {
    labels: assigneeNames.map(n => n.length > 15 ? n.substring(0, 15) + '...' : n),
    datasets: [
      {
        label: 'Active',
        data: assigneeNames.map(n => assigneeWise[n].open),
        backgroundColor: '#FF9800CC',
        borderColor: '#FF9800',
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Resolved/Closed',
        data: assigneeNames.map(n => assigneeWise[n].resolved),
        backgroundColor: '#4CAF50CC',
        borderColor: '#4CAF50',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const assigneeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
    },
  };

  // Ticket creation trend (line chart)
  const trendDays = Object.keys(dailyTrend).sort();
  const trendChartData = {
    labels: trendDays.map(d => {
      const date = new Date(d);
      return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' });
    }),
    datasets: [{
      label: 'Tickets Created',
      data: trendDays.map(d => dailyTrend[d]),
      borderColor: '#704493',
      backgroundColor: 'rgba(112, 68, 147, 0.08)',
      borderWidth: 2,
      pointBackgroundColor: '#704493',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
      fill: true,
    }],
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 45 } },
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: '#f1f5f9' } },
    },
  };

  const getStatusBadgeClass = (status) => {
    const map = {
      OPEN: 'badge-blue', ASSIGNED: 'badge-orange', IN_PROGRESS: 'badge-purple',
      RESOLVED: 'badge-green', CLOSED: 'badge-gray', REOPENED: 'badge-red',
    };
    return map[status] || 'badge-gray';
  };

  const getPriorityBadgeClass = (priority) => {
    const map = { LOW: 'badge-green', MEDIUM: 'badge-orange', HIGH: 'badge-red', CRITICAL: 'badge-purple' };
    return map[priority] || 'badge-gray';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Unique cities from ticket data (for filter dropdown)
  const ticketCities = [...new Set(allTickets.map(t => t.city || 'Unknown'))].sort();

  return (
    <div className="helpdesk-analytics">
      <div className="page-header">
        <div className="header-left">
          <MdBarChart className="header-icon" />
          <h2>Helpdesk Analytics</h2>
          {hasActiveFilters && (
            <span className="filter-active-badge">
              {filteredTickets.length} of {allTickets.length}
            </span>
          )}
        </div>
        <div className="header-actions">
          <button
            className={`btn btn-filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <MdFilterList />
            Filters
            {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
            {showFilters ? <MdExpandLess /> : <MdExpandMore />}
          </button>
          {hasActiveFilters && (
            <button className="btn btn-clear" onClick={clearFilters}>
              <MdClear /> Clear
            </button>
          )}
          <button className="btn btn-secondary" onClick={loadData}>
            <MdRefresh />
          </button>
        </div>
      </div>

      {/* Collapsible Filter Bar */}
      {showFilters && (
        <div className="filter-bar">
          <div className="filter-controls">
            <div className="filter-item">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="ALL">All Status</option>
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
                <option value="ALL">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div className="filter-item">
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="ALL">All Categories</option>
                {categories.map(c => (
                  <option key={c.id} value={c.categoryName}>{c.categoryName}</option>
                ))}
                <option value="Uncategorized">Uncategorized</option>
              </select>
            </div>
            <div className="filter-item">
              <select value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
                <option value="ALL">All Cities</option>
                {ticketCities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <select value={requestTypeFilter} onChange={e => setRequestTypeFilter(e.target.value)}>
                <option value="ALL">All Types</option>
                <option value="GENERAL_REQUEST">General Request</option>
                <option value="ID_CARD_REQUEST">ID Card Request</option>
              </select>
            </div>
            <div className="filter-item">
              <select value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}>
                <option value="ALL">All Assignees</option>
                <option value="UNASSIGNED">Unassigned</option>
                {assignees.map(a => (
                  <option key={a.email} value={a.email}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" />
            </div>
            <div className="filter-item">
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" />
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="stats-row">
        <div className={`stat-card stat-total ${statusFilter === 'ALL' ? '' : 'stat-dimmed'}`}
             onClick={() => setStatusFilter('ALL')} style={{ cursor: 'pointer' }}>
          <div className="stat-value">{totalTickets}</div>
          <div className="stat-label">Total</div>
        </div>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key}
               className={`stat-card stat-${key.toLowerCase()} ${statusFilter === key ? 'stat-selected' : ''} ${statusFilter !== 'ALL' && statusFilter !== key ? 'stat-dimmed' : ''}`}
               onClick={() => setStatusFilter(statusFilter === key ? 'ALL' : key)}
               style={{ cursor: 'pointer' }}>
            <div className="stat-value">{statusCounts[key] || 0}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
        <div className="stat-card stat-resolution">
          <div className="stat-value">{avgResolutionHours > 24 ? `${Math.round(avgResolutionHours / 24)}d` : `${avgResolutionHours}h`}</div>
          <div className="stat-label">Avg Resolution</div>
        </div>
      </div>

      {/* Charts Section */}
      {filteredTickets.length > 0 ? (
        <>
          {/* Row 1: Trend line (wide) */}
          {trendDays.length > 1 && (
            <div className="charts-grid">
              <div className="chart-card chart-wide">
                <h3>Ticket Creation Trend</h3>
                <div className="chart-container trend-chart-container">
                  <Line data={trendChartData} options={trendChartOptions} />
                </div>
              </div>
            </div>
          )}

          {/* Row 2: Status bar + Priority donut */}
          <div className="charts-grid">
            <div className="chart-card">
              <h3>Status Distribution</h3>
              <div className="chart-container hbar-chart-container">
                <Bar data={statusBarData} options={statusBarOptions} />
              </div>
            </div>

            {priorityLabels.length > 0 && (
              <div className="chart-card">
                <h3>Priority Breakdown</h3>
                <div className="chart-container donut-container">
                  <Doughnut data={priorityChartData} options={donutOptions} />
                </div>
              </div>
            )}
          </div>

          {/* Row 3: Category bar (wide) */}
          {categoryNames.length > 0 && (
            <div className="charts-grid">
              <div className="chart-card chart-wide">
                <h3>Tickets by Category & Status</h3>
                <div className="chart-container bar-chart-container">
                  <Bar data={categoryChartData} options={categoryChartOptions} />
                </div>
              </div>
            </div>
          )}

          {/* Row 4: Assignee workload + City + Request Type */}
          <div className="charts-grid charts-grid-3">
            {assigneeNames.length > 0 && (
              <div className="chart-card">
                <h3>Assignee Workload</h3>
                <div className="chart-container bar-chart-container">
                  <Bar data={assigneeChartData} options={assigneeChartOptions} />
                </div>
              </div>
            )}

            {cityLabels.length > 0 && (
              <div className="chart-card">
                <h3>Tickets by City</h3>
                <div className="chart-container donut-container">
                  <Doughnut data={cityChartData} options={donutOptions} />
                </div>
              </div>
            )}

            {typeLabels.length > 0 && (
              <div className="chart-card">
                <h3>Request Type Split</h3>
                <div className="chart-container donut-container">
                  <Doughnut data={typeChartData} options={donutOptions} />
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="no-data-card">
          <p>No tickets match the selected filters.</p>
          {hasActiveFilters && (
            <button className="btn btn-primary" onClick={clearFilters}>
              <MdClear /> Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Recent Unresolved Tickets */}
      {recentTickets.length > 0 && (
        <div className="recent-tickets-card">
          <h3><MdAccessTime /> Recent Unresolved Tickets</h3>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Requester</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentTickets.map((ticket, idx) => (
                  <tr key={idx}>
                    <td className="ticket-number">{ticket.ticketNumber}</td>
                    <td className="ticket-title">{ticket.title || '-'}</td>
                    <td>
                      <span className="type-badge">
                        {(ticket.requestType || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {ticket.priority && (
                        <span className={`priority-badge ${getPriorityBadgeClass(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(ticket.ticketStatus)}`}>
                        {STATUS_LABELS[ticket.ticketStatus] || ticket.ticketStatus}
                      </span>
                    </td>
                    <td>{ticket.requester?.email || '-'}</td>
                    <td>{ticket.assignedTo?.email || <span className="unassigned">Unassigned</span>}</td>
                    <td>{formatDate(ticket.createdAt)}</td>
                    <td>
                      <button
                        className="btn-icon btn-open"
                        title="Open ticket"
                        onClick={() => navigate(`/admin/helpdesk/tickets/${ticket.ticketNumber}`)}
                      >
                        <MdConfirmationNumber />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpdeskAnalytics;
